import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import StarIcon from '@mui/icons-material/StarRounded';
import StarBorderIcon from '@mui/icons-material/StarOutlineRounded';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import LockIcon from '@mui/icons-material/LockOutlined';
import UnlockIcon from '@mui/icons-material/LockOpenOutlined';
import { enqueueSnackbar } from 'notistack';
import { CLIP } from '../../enums';
import { useAppSelector } from '../../store/hooks';
import { ReactComponent as SendIcon } from '../../assets/icons/sendClipsIcon.svg';
import { useClipFavourites } from './context';

// A context menu must not change the row/multiple-clip selection just to star a clip.
export const useClipMenu = (
  clip: CLIP,
  onDelete: (event: React.MouseEvent<HTMLDivElement>) => void,
  blocked = false,
) => {
  const { ids, pending, loading, error, toggle, editor, actions, getAngleLockClips, setAngleLocks } = useClipFavourites();
  const currentAngle = useAppSelector((state) => state.sharedStore.currentVideoThatInFrame);
  const [savingAngle, setSavingAngle] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const close = () => setPosition(null);
  const favourite = ids.has(String(clip.id));
  const angleClips = position ? getAngleLockClips(clip) : [clip];
  const angleLocked = angleClips.length > 1
    ? angleClips.every((selected) => selected.locked_angle_id != null && String(selected.locked_angle_id) === String(currentAngle?.id))
    : clip.locked_angle_id != null;
  const changeAngleLock = async () => {
    if (savingAngle || !actions.current || (!angleLocked && !currentAngle)) return;
    setSavingAngle(true);
    try {
      const saved = await setAngleLocks(angleClips, angleLocked ? null : Number(currentAngle!.id));
      if (!saved) return;
      enqueueSnackbar(angleClips.length > 1
        ? `Angle ${angleLocked ? 'unlocked' : 'locked'} for ${angleClips.length} selected clips`
        : angleLocked ? 'Angle unlocked' : 'Angle locked for this clip', { variant: 'success' });
      close();
    } catch {
      enqueueSnackbar('Could not save angle locks. Please try again.', { variant: 'error' });
    } finally {
      setSavingAngle(false);
    }
  };
  const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (blocked) return;
    actions.current?.pause();
    setPosition({ top: event.clientY, left: event.clientX });
  };
  const menuItemSx = {
    '&&': {
      display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', alignItems: 'center', gap: '10px',
      width: '100%', minHeight: 34, m: 0, px: 1.25, py: 0.75,
      fontSize: 13, fontWeight: 500, lineHeight: '18px', textAlign: 'left', whiteSpace: 'normal',
    },
    '& > svg': { display: 'block', width: 18, height: 18, margin: 0 },
  };
  const menu = position && (
    <Menu open onClose={close} anchorReference="anchorPosition" anchorPosition={position}
      PaperProps={{ sx: { width: 240, maxWidth: 'calc(100vw - 32px)' } }}
      MenuListProps={{ sx: { p: '6px', gap: '2px', alignItems: 'stretch' } }}
      onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
      <MenuItem sx={menuItemSx} disabled={loading || error || pending.has(String(clip.id))} onClick={() => { toggle(clip.id); close(); }}>
        {favourite ? <StarIcon sx={{ color: '#ffd166' }} /> : <StarBorderIcon />}
        <span>{favourite ? 'Remove from favourites' : 'Add to favourites'}</span>
      </MenuItem>
      {editor.canSend && <MenuItem sx={menuItemSx} onClick={() => { actions.current?.send([clip]); close(); }}><SendIcon /><span>Send clip to composer</span></MenuItem>}
      {editor.canEdit && <MenuItem sx={menuItemSx} disabled={savingAngle || (!angleLocked && !angleClips.every((selected) => actions.current?.canLockAngle(selected)))} onClick={changeAngleLock}>
        {angleLocked ? <UnlockIcon /> : <LockIcon />}
        <span>{savingAngle ? 'Saving angle…' : `${angleLocked ? 'Unlock angle' : 'Lock angle'}${angleClips.length > 1 ? ` for ${angleClips.length} selected clips` : ''}`}</span>
      </MenuItem>}
      {editor.canEdit && <MenuItem sx={menuItemSx} onClick={() => { actions.current?.edit(clip); close(); }}><EditIcon /><span>Edit clip</span></MenuItem>}
      {editor.canEdit && <MenuItem sx={menuItemSx} component="div" onClick={(event) => { onDelete(event); close(); }}><DeleteIcon /><span>Delete clip</span></MenuItem>}
    </Menu>
  );
  return { onContextMenu, menu };
};
